import React, { useCallback } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { CSV_FILE_UPLOAD_TYPE, CSV_MAX_SIZE_BYTES } from '../../../common/constants';
import { UploadButton } from '../../../common/ui/upload-button';
import {
	useMappingState,
	useMigrationId,
	useProjectData,
	useSource,
	useUploadedFileName,
} from '../../../controllers/import-state/selectors';
import { useUploadCsv } from '../../../services/upload-csv';
import messages from './messages';

export const UploadCSV = () => {
	const { formatMessage } = useIntl();
	const [source] = useSource();
	const { isFileUploading, hasErrorOccurred, uploadCsv, abort } = useUploadCsv(source);
	const [, { setMigrationId }] = useMigrationId();
	const [, { setMappingState, resetProjectData }] = useMappingState();
	const [uploadedFileName, { setUploadedFileName, setUploadedConfigName }] = useUploadedFileName();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ key }] = useProjectData();

	const onReceiveFile = useCallback(
		(file: File) => {
			setMappingState(null);
			fireUIAnalytics(createAnalyticsEvent({}), 'csvFile uploading', { fileSize: file.size });
			uploadCsv(file, key).then((migrationId) => {
				setMigrationId(migrationId);
				resetProjectData();
				if (migrationId) {
					setUploadedFileName(file?.name ?? null);
					fireTrackAnalytics(createAnalyticsEvent({}), 'csvFile uploaded', {
						fileSize: file.size,
					});
				}
			});
		},
		[
			createAnalyticsEvent,
			key,
			resetProjectData,
			setMappingState,
			setMigrationId,
			setUploadedFileName,
			uploadCsv,
		],
	);

	const onCancel = useCallback(() => {
		setUploadedFileName(null);
		setUploadedConfigName(null);
		abort();
	}, [abort, setUploadedConfigName, setUploadedFileName]);

	return (
		<Box xcss={wrapperStyles}>
			<UploadButton
				uploadText={formatMessage(messages.uploadCsvNonFinal)}
				acceptType={CSV_FILE_UPLOAD_TYPE}
				onReceiveFile={onReceiveFile}
				onCancel={onCancel}
				fileSizeLimit={CSV_MAX_SIZE_BYTES}
				fileName={uploadedFileName}
				isFileUploading={isFileUploading}
				hasErrorOccured={hasErrorOccurred}
			/>
		</Box>
	);
};

const wrapperStyles = xcss({
	paddingBlock: 'space.500',
});
